import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSnapshotData } from "src/redux/slices/generatorSlice";
import { getYachtId } from "src/utils";
import { hasFlag } from "src/utils/flagUtils";
import {
  getWidgetHeaders,
  getWidgetRows,
} from "src/components/pages/Home/CustomDashboard/Widget.utils";

interface UseFetchSnapshotReturn {
  pageSize: number;
  setPageSize: (size: number) => void;
}

/**
 * Hook to fetch historical snapshot data when showSnapshotEntry flag is present
 */
export const useFetchSnapshot = (): UseFetchSnapshotReturn => {
  const [pageSize, setPageSize] = useState(25);
  const { logbookId } = useParams();
  const dispatch = useDispatch();
  const { flags } = useSelector(state => state.generatorSlice);

  const shouldFetchSnapshot = hasFlag(flags, "showSnapshotEntry");

  // Fetch snapshot data from get-preview endpoint
  const { data: snapshotData, refetch } = useGetApi<any>({
    request: "get-preview",
    body: {
      params: {
        lbid: logbookId,
        yachtid: getYachtId(),
        page: 1,
        pageMax: pageSize,
      },
    },
    fetchOnInit: false,
  });

  // Trigger snapshot fetch when flag is detected
  useEffect(() => {
    if (shouldFetchSnapshot) {
      refetch();
    }
  }, [shouldFetchSnapshot, refetch, pageSize]);

  // Process and store snapshot data when available
  useEffect(() => {
    if (shouldFetchSnapshot && snapshotData?.success && snapshotData.list) {
      const headers = ["Watchkeeper", "Events"].includes(
        snapshotData.lbname ?? "",
      )
        ? getWidgetHeaders(snapshotData.headers, true)
        : snapshotData.headers;

      const list =
        snapshotData.list.length > 0 &&
        typeof snapshotData?.list[0] === "object" &&
        "version" in snapshotData?.list[0]
          ? getWidgetRows(snapshotData.list)
          : snapshotData.list;

      dispatch(
        setSnapshotData({
          snapshotHeaders: headers,
          snapshotEntry: {
            list,
            lbname: snapshotData.lbname,
          },
          flags,
        }),
      );
    }
  }, [snapshotData, shouldFetchSnapshot, dispatch, flags]);

  return { pageSize, setPageSize };
};
